/**
 * Phase and activity names in infra projects carry their WBS code inside the
 * free-text name column — there is no separate code field to read
 * (`phaseSequenceId` holds system-generated values like `URIPHS000018`).
 *
 * The code shows up two ways:
 *   "HRT - Adits Excavation(HEP-B1.7.1.1)"   <- parenthetical suffix (common)
 *   "HEP-B1.7.4.5.5  TRT - Concrete Lining"  <- leading token (rare)
 *
 * `splitWbsCode` separates the two so a screen can show the trimmed label when
 * codes are switched off, and fall back to the raw stored name when they're on.
 * Names that merely happen to contain parentheses — "Surge Shaft (SS)",
 * "Downstream (D/S) ...", "HRT FACE-1 ... (From Adit-1A)" — survive untouched.
 */

export interface SplitName {
  /** The name with the WBS code removed. Never empty unless the input was. */
  label: string;
  /** The extracted WBS code, or null when the name doesn't carry one. */
  code: string | null;
}

/** e.g. HEP-B1.7.1.1, AE-0101, B1.7.4.5.5 — letters, hyphen, dotted path. */
const CODE_SHAPE = /^[A-Za-z]{2,}-[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*$/;

/** Trailing "(...)" group, capturing everything before it. */
const TRAILING_PAREN = /^(.*)\(([^()]*)\)\s*$/;

/** Leading "CODE " token, capturing the remainder of the name. */
const LEADING_TOKEN = /^([A-Za-z]{2,}-[A-Za-z0-9.]+)\s+(.+)$/;

/**
 * A token only counts as a WBS code when it has no whitespace, matches the
 * code shape, and contains a digit. The digit rule is what keeps ordinary
 * hyphenated words out — "TRT-Adit", "HRT-Concrete".
 *
 * @param requireDot demanded for leading tokens only. Without it
 * "ADIT-3A EXCAVATION" would lose its "ADIT-3A"; real leading codes are
 * always dotted paths like "HEP-B1.7.4.5.5".
 */
const isWbsCode = (token: string, requireDot = false): boolean => {
  if (!token || /\s/.test(token)) return false;
  if (!CODE_SHAPE.test(token)) return false;
  if (!/\d/.test(token)) return false;
  if (requireDot && !token.includes(".")) return false;
  return true;
};

export function splitWbsCode(name?: string | null): SplitName {
  const raw = (name ?? "").trim();
  if (!raw) return { label: "", code: null };

  // Suffix first: "ADIT-3A EXCAVATION(AE-0102)" must resolve via the
  // parenthetical, not by stripping the "ADIT-3A" that opens the name.
  const trailing = raw.match(TRAILING_PAREN);
  if (trailing && isWbsCode(trailing[2])) {
    const label = trailing[1].trim();
    if (label) return { label, code: trailing[2] };
    // Name was nothing but a code — keep it visible.
    return { label: raw, code: null };
  }

  const leading = raw.match(LEADING_TOKEN);
  if (leading && isWbsCode(leading[1], true)) {
    return { label: leading[2].trim(), code: leading[1] };
  }

  return { label: raw, code: null };
}

/** Convenience for the many call sites that only want the clean name. */
export const wbsLabel = (name?: string | null): string =>
  splitWbsCode(name).label;

/* -------------------------------------------------------------------------- */
/* Area prefixes                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Tunnel-system prefixes that repeat down a whole branch of the WBS and carry
 * no information once you can see which phase you're under. Extend as other
 * projects turn up their own. Uppercase-only by design — matching loosely would
 * eat ordinary words.
 */
export const AREA_PREFIXES = ["HRT", "TRT"];

/**
 * Only strips when a hyphen follows the token, so "HRT - Adits Excavation" and
 * "TRT-Adit Excavation" lose it while "HRT FACE-1 0 TO 400 RD Excavation"
 * keeps it — there the token is part of the sentence, not a prefix.
 */
const AREA_PREFIX_RE = new RegExp(`^(?:${AREA_PREFIXES.join("|")})\\s*-\\s*`);

export const stripAreaPrefix = (label: string): string => {
  const stripped = label.replace(AREA_PREFIX_RE, "").trim();
  // Never strip a name down to nothing.
  return stripped || label;
};

/** Code removed and area prefix removed — the plain reading of a name. */
export const displayName = (name?: string | null): string =>
  stripAreaPrefix(splitWbsCode(name).label);

interface LabelNode {
  key?: string;
  name?: string | null;
  children?: LabelNode[];
}

/**
 * Builds the codes-off label for every phase/activity node in a tree, keyed by
 * node key.
 *
 * Area prefixes are dropped, *except* where dropping one would make two
 * siblings read the same — "HRT - Concrete Lining" and "TRT - Concrete Lining"
 * both collapse to "Concrete Lining", so both keep their prefix rather than
 * become indistinguishable. The check is per sibling group, so an unambiguous
 * name elsewhere in the tree still gets the short form.
 *
 * Task nodes are skipped: their names are already clean and the Tasks tree
 * renders them verbatim.
 */
export function buildCleanLabelMap(nodes?: LabelNode[]): Map<string, string> {
  const labels = new Map<string, string>();

  const walk = (siblings: LabelNode[]) => {
    const group = (siblings || []).filter(
      (n) => n?.key && !String(n.key).includes("T-")
    );

    const entries = group.map((node) => {
      const base = splitWbsCode(node.name).label;
      return { node, base, short: stripAreaPrefix(base) };
    });

    const shortCounts = new Map<string, number>();
    entries.forEach(({ short }) =>
      shortCounts.set(short, (shortCounts.get(short) || 0) + 1)
    );

    entries.forEach(({ node, base, short }) => {
      const clashes = (shortCounts.get(short) || 0) > 1;
      labels.set(String(node.key), clashes ? base : short);
      if (node.children?.length) walk(node.children);
    });
  };

  walk(nodes || []);
  return labels;
}
